import React, { useEffect, useMemo, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { CardElement, Elements, useElements, useStripe } from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";
import {
  ArrowRight,
  BadgeDollarSign,
  BriefcaseBusiness,
  Building2,
  CalendarClock,
  Check,
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  Clock3,
  CreditCard,
  Eye,
  FileText,
  Filter,
  Landmark,
  LockKeyhole,
  Plus,
  ReceiptText,
  RefreshCw,
  Search,
  ShieldCheck,
  TrendingUp,
  WalletCards,
  X,
} from "lucide-react";
import Modal from "react-bootstrap/Modal";
import canViewInvestmentProject from "./projectVisibilityAccess";
import { isAuthenticated } from "../../auth";
import { AttachPaymentMethod, listePM_createStripeCusTomer, retrievePayment } from "../../auth/stripe";
import { confirmInvestmentPayment, createInvestmentPaymentIntent, previewInvestment } from "../../auth/apiInvestment";
import { REACT_PK } from "../../../config";
import { getCurrentWallRoleName } from "../Rolle";
import Pages from "../../pages/LanguagePage";
import "./InvestmentSections.css";
import { elakayAutoText } from "../dataElakay";
import AddressLocationSelector from "../addressLocation/AddressLocationSelector";
import { toLocationValue, fromLocationValue, STRIPE_FIELDS } from "../addressLocation/addressLocationData";
import { toFileUrl } from "../../../utils/fileUrl";

const stripePromise = REACT_PK ? loadStripe(REACT_PK) : null;

const TYPE_LABELS = {
  equity: "Equity",
  revenue_share: "Revenue sharing",
  business_deal: "Business deal",
  production: "Production",
  digital: "Digital",
  marketplace: "Marketplace",
  real_estate: "Real estate",
  fixed_term: "Fixed term",
  other: "Autre",
};

const DOCUMENT_TYPE_LABELS = {
  get description() { return elakayAutoText("h_55f8ebc805e65b", "Description"); },
  contract: "Contrat",
  pitch_deck: "Pitch deck",
  financials: "Finances",
  terms: "Terms",
  other: "Autre",
};

const DOCUMENT_KIND_LABELS = {
  pdf: "PDF",
  doc: "DOC",
  docx: "DOCX",
  html: "HTML",
  image: "Image",
  document: "Document",
};

const MODE_LABELS = {
  limited: "Limite",
  unlimited: "Illimite",
};

const FREQUENCY_LABELS = {
  one_time: "Paiement unique",
};

const getPaymentFrequencyLabel = () => FREQUENCY_LABELS.one_time;

const ACCESS_GROUP_LABELS = {
  vip_investors: "VIP Investors",
  family_group: "Investors Elakay Group",
  administrator_group: "Administrator Group",
  associate_group: "Associate Group",
  seller_group: "Seller Group",
  provider_group: "Provider Group",
};

const formatCurrency = (value = 0, currency = "USD") => {
  try {
    return new Intl.NumberFormat("fr-FR", {
      style: "currency",
      currency,
      maximumFractionDigits: 0,
    }).format(Number(value) || 0);
  } catch {
    return `${Number(value || 0).toLocaleString()} ${currency}`;
  }
};

const formatDate = (value) => {
  if (!value) return "A definir";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "A definir";
  return date.toLocaleDateString("fr-FR", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
};

// Client-side preview only, identical formula to the backend's
// calculateProportionalValue (elakay_services/roleActivation.js) -- the
// backend independently recomputes and persists the real capacity at
// investment time, this is purely display/preview.
export const calculateProportionalValue = (investedAmount, minAmount, maxAmount, minValue, maxValue) => {
  const safeMinAmount = Math.max(0, Number(minAmount) || 0);
  const safeMaxAmount = Math.max(safeMinAmount, Number(maxAmount) || safeMinAmount);
  const safeMinValue = Math.max(0, Math.round(Number(minValue) || 0));
  const safeMaxValue = Math.max(safeMinValue, Math.round(Number(maxValue) || 0));
  if (safeMaxAmount <= safeMinAmount) return safeMaxValue || safeMinValue;
  const ratio = Math.min(1, Math.max(0, (Number(investedAmount) - safeMinAmount) / (safeMaxAmount - safeMinAmount)));
  return Math.round(safeMinValue + ratio * (safeMaxValue - safeMinValue));
};

const getProgress = (project = {}) => {
  const direct = Number(project.fundingPercentage);
  if (Number.isFinite(direct)) return Math.max(0, Math.min(100, direct));
  const goal = Number(project.fundingGoal || 0);
  if (!goal) return 0;
  return Math.max(0, Math.min(100, (Number(project.collectedAmount || 0) / goal) * 100));
};

// Uploaded images were stored as /documents/<f> (never served in
// production): toFileUrl maps them to /elakay_api/files/documents/<f>.
const getProjectImage = (project = {}) => {
  if (project.thumbnail) return toFileUrl(project.thumbnail);
  const first = project.gallery?.[0];
  if (typeof first === "string") return toFileUrl(first);
  return toFileUrl(first?.url || "");
};

const getProjectImageOpacity = (project = {}) => {
  const number = Number(project.thumbnailOpacity);
  if (!Number.isFinite(number)) return 1;
  return Math.min(1, Math.max(0, number));
};

const getProjectTypeLabel = (project = {}) => {
  if (project.projectType === "other" && project.projectTypeOtherTitle) {
    return project.projectTypeOtherTitle;
  }
  return TYPE_LABELS[project.projectType] || project.projectType;
};

const getDocumentTypeLabel = (document = {}) =>
  document.documentType === "other" && document.documentTypeTitle
    ? document.documentTypeTitle
    : DOCUMENT_TYPE_LABELS[document.documentType] || document.documentType || "Document";

const getDocumentTitle = (document = {}, fallback = "Document") =>
  document.title || document.originalName || document.filename || fallback;

const getDocumentKindLabel = (document = {}) => {
  const kind = String(document.kind || "").toLowerCase();
  if (DOCUMENT_KIND_LABELS[kind]) return DOCUMENT_KIND_LABELS[kind];
  const url = String(document.url || "");
  const extension = url.split("?")[0].split(".").pop()?.toLowerCase();
  if (DOCUMENT_KIND_LABELS[extension]) return DOCUMENT_KIND_LABELS[extension];
  if (["jpg", "jpeg", "png", "webp", "gif", "svg"].includes(extension)) return "Image";
  return "Document";
};

const OFFICE_DOCUMENT_EXTENSIONS = ["doc", "docx"];

const isOfficeDocumentUrl = (url = "", kind = "") => {
  const normalizedKind = String(kind || "").toLowerCase();
  if (OFFICE_DOCUMENT_EXTENSIONS.includes(normalizedKind)) return true;
  const extension = String(url).split("?")[0].split(".").pop()?.toLowerCase();
  return OFFICE_DOCUMENT_EXTENSIONS.includes(extension);
};

const getContractDocument = (project = {}) => {
  const documents = Array.isArray(project.documents) ? project.documents : [];
  const attachedContract = documents.find((document) => document?.documentType === "contract");
  if (attachedContract) return attachedContract;
  if (project.contractPDF) {
    return {
      title: elakayAutoText("h_d008bdd9b9811d", "Contrat"),
      documentType: "contract",
      kind: "pdf",
      url: project.contractPDF,
    };
  }
  return null;
};

const hasRichDocument = (doc) => {
  if (typeof doc === "string") return Boolean(doc.trim());
  return Array.isArray(doc?.content) && doc.content.length > 0;
};

const getPriorityAccessLabel = (project = {}) => {
  const accessState = project.visibilityAccessState || {};
  if (accessState.reason !== "group_access") return "";
  const matchedGroups = Array.isArray(accessState.matchedGroupKeys) ? accessState.matchedGroupKeys : [];
  const groupLabels = matchedGroups
    .map((groupKey) => ACCESS_GROUP_LABELS[groupKey] || groupKey)
    .filter(Boolean);

  return groupLabels.length
    ? `Acces prioritaire: ${groupLabels.join(", ")}`
    : "Acces prioritaire";
};

const backgroundImageStyle = (image) =>
  image ? { backgroundImage: `url("${String(image).replace(/"/g, '\\"')}")` } : undefined;

const compactText = (value = "", max = 150) => {
  const text = String(value || "").trim();
  if (text.length <= max) return text;
  return `${text.slice(0, max - 1).trim()}...`;
};

const getNodeText = (node) => {
  if (!node) return "";
  if (typeof node === "string") return node;
  if (node.text) return node.text;
  if (Array.isArray(node.content)) return node.content.map(getNodeText).join(" ");
  return "";
};

const renderRichDocument = (doc, fallback) => {
  if (typeof doc === "string" && doc.trim()) return <p>{doc}</p>;
  const content = Array.isArray(doc?.content) ? doc.content : [];
  if (!content.length) return <p>{fallback}</p>;

  return content.slice(0, 12).map((node, index) => {
    const text = getNodeText(node).trim();
    if (!text) return null;
    if (node.type === "heading") return <h3 key={index}>{text}</h3>;
    if (node.type === "bulletList" || node.type === "orderedList") {
      const items = (node.content || []).map((item, itemIndex) => {
        const itemText = getNodeText(item).trim();
        return itemText ? <li key={itemIndex}>{itemText}</li> : null;
      });
      return <ul key={index}>{items}</ul>;
    }
    return <p key={index}>{text}</p>;
  });
};

const normalizeCatalog = (data) => {
  if (Array.isArray(data)) return { projects: data, count: data.length, facets: {} };
  return {
    projects: data?.projects || [],
    count: data?.count || data?.projects?.length || 0,
    facets: data?.facets || {},
    skip: data?.skip || 0,
    limit: data?.limit || 12,
  };
};

const filterUrl = (location, updates = {}) => {
  const params = new URLSearchParams(location.search);
  Object.entries(updates).forEach(([key, value]) => {
    if (value === "" || value === null || value === undefined) params.delete(key);
    else params.set(key, value);
  });
  params.delete("skip");
  const query = params.toString();
  return `/investments${query ? `?${query}` : ""}`;
};

const paginationUrl = (location, skip) => {
  const params = new URLSearchParams(location.search);
  if (skip > 0) params.set("skip", skip);
  else params.delete("skip");
  const query = params.toString();
  return `/investments${query ? `?${query}` : ""}`;
};

const CARD_ELEMENT_OPTIONS = {
  style: {
    base: {
      color: "#10201c",
      fontFamily: "Roboto, system-ui, -apple-system, BlinkMacSystemFont, sans-serif",
      fontSize: "15px",
      "::placeholder": {
        color: "#7b8f87",
      },
    },
    invalid: {
      color: "#a42b2b",
    },
  },
};

const EMPTY_BILLING_DETAILS = {
  name: "",
  line1: "",
  city: "",
  state: "",
  postal_code: "",
  country: "",
};

const getPaymentCard = (paymentMethod = {}) => paymentMethod.card || paymentMethod;

const getPaymentBrand = (paymentMethod = {}) => {
  const card = getPaymentCard(paymentMethod);
  return String(card.brand || paymentMethod.brand || "card").toUpperCase();
};

const getPaymentLast4 = (paymentMethod = {}) => {
  const card = getPaymentCard(paymentMethod);
  return card.last4 || paymentMethod.last4 || "----";
};

const getPaymentExpiry = (paymentMethod = {}) => {
  const card = getPaymentCard(paymentMethod);
  const month = card.exp_month || paymentMethod.exp_month;
  const year = card.exp_year || paymentMethod.exp_year;
  return month && year ? `${month}/${year}` : "--/--";
};

const getBillingFromPaymentMethod = (paymentMethod = {}) => {
  const billing = paymentMethod.billing_details || {};
  const address = billing.address || {};
  return {
    name: billing.name || "",
    line1: address.line1 || "",
    city: address.city || "",
    state: address.state || "",
    postal_code: address.postal_code || "",
    country: address.country || "",
  };
};

const mergeBillingDetails = (current, next) => ({
  ...current,
  ...Object.fromEntries(
    Object.entries(next || {}).filter(([, value]) => value !== undefined && value !== null && value !== "")
  ),
});

const InvestmentPaymentModalFrame = (props) => (
  <Elements stripe={stripePromise}>
    <InvestmentPaymentModal {...props} />
  </Elements>
);

const InvestmentPaymentModal = ({
  project,
  amount,
  authToken,
  onClose,
  onSubmitPayment,
  submitting,
}) => {
  const stripe = useStripe();
  const elements = useElements();
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [selectedMethodId, setSelectedMethodId] = useState("");
  const [selectedMethodDetails, setSelectedMethodDetails] = useState(null);
  const [paymentMode, setPaymentMode] = useState("saved");
  const [billingDetails, setBillingDetails] = useState(EMPTY_BILLING_DETAILS);
  const [paymentNote, setPaymentNote] = useState("");
  const [saveCard, setSaveCard] = useState(true);
  const [loadingMethods, setLoadingMethods] = useState(true);
  const [localSubmitting, setLocalSubmitting] = useState(false);
  const [paymentError, setPaymentError] = useState("");

  // Step 1 -- "Investment information": server-authoritative preview,
  // fetched once when the modal opens (the amount can't be edited from
  // inside this modal, so a single fetch per mount is enough). Step 2 keeps
  // the exact Stripe flow that already existed here.
  const [step, setStep] = useState(1);
  const [preview, setPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(true);
  const [previewError, setPreviewError] = useState("");

  const amountNumber = Number(amount) || 0;
  const isSubmitting = submitting || localSubmitting;

  useEffect(() => {
    let active = true;
    setPreviewLoading(true);
    setPreviewError("");
    previewInvestment(project._id, { amount: amountNumber, acceptedContract: true }, authToken)
      .then((data) => {
        if (!active) return;
        if (data?.ok === false) {
          setPreviewError(data.error || elakayAutoText("h_f34b05f17fdfbc", "Le nouveau total depasse le maximum autorise pour ce projet."));
        }
        setPreview(data);
      })
      .catch((error) => {
        if (!active) return;
        setPreviewError(error.message || elakayAutoText("h_2b00caaad7c513", "Impossible de calculer la previsualisation de l'investissement."));
      })
      .finally(() => {
        if (active) setPreviewLoading(false);
      });
    return () => {
      active = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [project?._id, amountNumber, authToken]);

  const canContinueToPayment = Boolean(preview) && preview?.ok !== false && !previewLoading;

  useEffect(() => {
    let active = true;

    const loadSelectedPaymentMethod = async (methodId) => {
      if (!methodId) return;
      const data = await retrievePayment(authToken, { IDpaymentMethods: methodId });
      if (!active) return;
      if (data?.error) throw new Error(data.error);
      const paymentMethod = data?.payment_method || data;
      setSelectedMethodDetails(paymentMethod);
      setBillingDetails((prev) => mergeBillingDetails(prev, getBillingFromPaymentMethod(paymentMethod)));
    };

    const loadPaymentMethods = async () => {
      setLoadingMethods(true);
      setPaymentError("");
      try {
        const data = await listePM_createStripeCusTomer(authToken, "?customer=true");
        if (!active) return;
        if (data?.error) throw new Error(data.error);
        const methods = Array.isArray(data?.paymentMethods) ? data.paymentMethods : [];
        setPaymentMethods(methods);
        const defaultId = data?.defaultPaymentMethodId || methods[0]?.id || "";
        if (defaultId) {
          setPaymentMode("saved");
          setSelectedMethodId(defaultId);
          await loadSelectedPaymentMethod(defaultId);
        } else {
          setPaymentMode("new");
        }
      } catch (error) {
        if (!active) return;
        setPaymentMode("new");
        setPaymentError(error.message || elakayAutoText("h_8021c36f79b957", "Impossible de charger les moyens de paiement."));
      } finally {
        if (active) setLoadingMethods(false);
      }
    };

    loadPaymentMethods();
    return () => {
      active = false;
    };
  }, [authToken]);

  // Escape-to-close (guarded by isSubmitting) is now handled by react-bootstrap's
  // Modal via the keyboard prop -- see the <Modal keyboard={!isSubmitting}> below.

  const loadPaymentMethodDetails = async (methodId) => {
    if (!methodId) return;
    setPaymentError("");
    setSelectedMethodId(methodId);
    setSelectedMethodDetails(null);
    try {
      const data = await retrievePayment(authToken, { IDpaymentMethods: methodId });
      if (data?.error) throw new Error(data.error);
      const paymentMethod = data?.payment_method || data;
      setSelectedMethodDetails(paymentMethod);
      setBillingDetails((prev) => mergeBillingDetails(prev, getBillingFromPaymentMethod(paymentMethod)));
    } catch (error) {
      setPaymentError(error.message || elakayAutoText("h_84458a3d191fd7", "Impossible de lire cette carte."));
    }
  };

  const handleBillingChange = (field) => (event) => {
    setBillingDetails((prev) => ({ ...prev, [field]: event.target.value }));
  };

  const getPayButtonReason = () => {
    if (!authToken) return elakayAutoText("h_a73e893fddae61", "Connexion requise.");
    if (amountNumber <= 0) return elakayAutoText("h_e1d3c8f4466b6e", "Montant d'investissement invalide.");
    if (paymentMode === "saved" && !selectedMethodId) {
      return elakayAutoText("h_a277aa2a4bba3b", "Selectionnez une methode de paiement.");
    }
    if (paymentMode === "new" && !stripePromise) {
      return elakayAutoText("h_de4356124119d0", "Stripe n'est pas configure.");
    }
    if (paymentMode === "new" && !billingDetails.name.trim()) {
      return elakayAutoText("h_2997f9a102dcb2", "Ajoutez le nom sur la carte.");
    }
    return "";
  };

  const handlePaymentSubmit = async () => {
    const reason = getPayButtonReason();
    if (reason) {
      setPaymentError(reason);
      return;
    }

    setPaymentError("");
    setLocalSubmitting(true);
    try {
      let paymentMethodId = selectedMethodId;
      let createdPaymentMethod = null;

      if (paymentMode === "new") {
        if (!stripe || !elements) {
          throw new Error(elakayAutoText("h_797a9018218cc2", "Stripe n'est pas encore pret."));
        }

        const cardElement = elements.getElement(CardElement);
        const result = await stripe.createPaymentMethod({
          type: "card",
          card: cardElement,
          billing_details: {
            name: billingDetails.name,
            address: {
              line1: billingDetails.line1,
              city: billingDetails.city,
              state: billingDetails.state,
              postal_code: billingDetails.postal_code,
              country: billingDetails.country,
            },
          },
        });

        if (result.error) throw new Error(result.error.message);
        createdPaymentMethod = result.paymentMethod;
        paymentMethodId = createdPaymentMethod.id;

        if (saveCard) {
          const attachResult = await AttachPaymentMethod(authToken, { PM_id: paymentMethodId });
          if (attachResult?.error) throw new Error(attachResult.error);
        }
      }

      if (!paymentMethodId) {
        throw new Error(elakayAutoText("h_a277aa2a4bba3b", "Selectionnez une methode de paiement."));
      }

      await onSubmitPayment({
        paymentMethodId,
        paymentMode,
        paymentNote,
        billingDetails,
        selectedPaymentMethod: selectedMethodDetails || createdPaymentMethod,
        stripe,
      });
    } catch (error) {
      setPaymentError(error.message || elakayAutoText("h_fe164b7760e10a", "Impossible de preparer le paiement."));
    } finally {
      setLocalSubmitting(false);
    }
  };

  const buttonReason = getPayButtonReason();
  const selectedCard = selectedMethodDetails || paymentMethods.find((method) => method.id === selectedMethodId);

  return (
    <Modal
      show
      onHide={onClose}
      centered
      backdrop={isSubmitting ? "static" : true}
      keyboard={!isSubmitting}
      dialogClassName="investment-payment-dialog"
      contentClassName="investment-payment-modal"
      backdropClassName="investment-payment-backdrop-bs"
      aria-labelledby="investment-payment-title"
    >
        <div className="investment-payment-modal__header">
          <div>
            <span><LockKeyhole size={15} />{elakayAutoText("h_01b33ca0311e09", "Paiement securise")}</span>
            <h2 id="investment-payment-title">{elakayAutoText("h_a71d7f88771d33", "Paiement investissement")}</h2>
          </div>
          <button type="button" className="investment-modal-close" onClick={onClose} disabled={isSubmitting} aria-label={elakayAutoText("h_d8d0378aeb8f91", "Fermer")}>
            <X size={20} />
          </button>
        </div>

        <div className="investment-payment-steps" role="list" aria-label={elakayAutoText("h_a2bf9bbb4dc2b3", "Resume de l'investissement")}>
          <div className={`investment-payment-step ${step === 1 ? "is-active" : "is-done"}`} role="listitem">
            <span className="investment-payment-step__badge">1</span>
            <span>{elakayAutoText("h_0127e499cf1996", "Informations sur l'investissement")}</span>
          </div>
          <ChevronRight size={16} className="investment-payment-step__arrow" />
          <div className={`investment-payment-step ${step === 2 ? "is-active" : ""}`} role="listitem">
            <span className="investment-payment-step__badge">2</span>
            <span>{elakayAutoText("h_2ac8f483b1968d", "Paiement securise")}</span>
          </div>
        </div>

        {step === 1 && (
          <div className="investment-payment-modal__body investment-payment-modal__body--info">
            <section className="investment-payment-summary">
              <div className="investment-payment-summary__title">
                <ReceiptText size={18} />
                <h3>{elakayAutoText("h_a2bf9bbb4dc2b3", "Resume de l'investissement")}</h3>
              </div>
              <div className="investment-payment-line">
                <span>{elakayAutoText("h_17ff00bca5295c", "Projet")}</span>
                <strong>{project.title}</strong>
              </div>
              <div className="investment-payment-line">
                <span>{elakayAutoText("h_5d398eef3ecc3e", "Minimum")}</span>
                <strong>{formatCurrency(preview?.minimumInvestment ?? project.minimumInvestment, project.currency)}</strong>
              </div>
              <div className="investment-payment-line">
                <span>{elakayAutoText("h_a8df2fec28d894", "Maximum")}</span>
                <strong>{preview?.maximumInvestment ? formatCurrency(preview.maximumInvestment, project.currency) : elakayAutoText("h_8bb749a035fbbe", "Flexible")}</strong>
              </div>
              <div className="investment-payment-line">
                <span>{elakayAutoText("h_e7245bda285a97", "Montant investi")}</span>
                <strong>{formatCurrency(amountNumber, project.currency)}</strong>
              </div>
            </section>

            {previewLoading && (
              <div className="investment-payment-loading">
                <RefreshCw size={16} />
                {elakayAutoText("h_6c3f3bde3c709d", "Calcul des informations d'investissement en cours...")}
              </div>
            )}

            {!previewLoading && previewError && (
              <div className="investment-payment-error">{previewError}</div>
            )}

            {!previewLoading && preview && (
              <>
                <section className="investment-payment-summary">
                  <div className="investment-payment-summary__title">
                    <TrendingUp size={18} />
                    <h3>{preview.previouslyInvested > 0
                      ? elakayAutoText("h_09c3ffc25e2a0a", "Avantages apres ce paiement")
                      : elakayAutoText("h_4efbc2aa3d7f9c", "Ceci est votre premier investissement dans ce projet.")}</h3>
                  </div>
                  <div className="investment-payment-line">
                    <span>{elakayAutoText("h_5e5228785944f5", "Deja investi dans ce projet")}</span>
                    <strong>{formatCurrency(preview.previouslyInvested, project.currency)}</strong>
                  </div>
                  <div className="investment-payment-line">
                    <span>{elakayAutoText("h_9f62da8fd991b7", "Nouveau total apres ce paiement")}</span>
                    <strong>{formatCurrency(preview.projectedTotalInvestment, project.currency)}</strong>
                  </div>
                  {preview.remainingAfterInvestment !== null && (
                    <div className="investment-payment-line">
                      <span>{elakayAutoText("h_ed9c15ed063528", "Montant restant disponible apres paiement")}</span>
                      <strong>{formatCurrency(preview.remainingAfterInvestment, project.currency)}</strong>
                    </div>
                  )}
                  <div className="investment-payment-line">
                    <span>{elakayAutoText("h_a300a232a198cd", "Pourcentage de propriete actuel")}</span>
                    <strong>{preview.currentPercentage}%</strong>
                  </div>
                  <div className="investment-payment-line">
                    <span>{elakayAutoText("h_62f26b8f05ad39", "Nouveau pourcentage estime")}</span>
                    <strong>{preview.projectedPercentage}%</strong>
                  </div>
                </section>

                {/* Only shown when the project can actually change/refresh the
                    user's investor status -- i.e. autoBecomeInvestor is on
                    and either this investment grants the role or the user
                    already holds it (server's investorQuotasApply rule,
                    mirrored here from preview.willGrantInvestorRole /
                    preview.currentRole so the two can't drift). A project
                    with autoBecomeInvestor off has nothing role-related to
                    show, so the whole card is skipped rather than showing
                    a "no change" message on every investment. */}
                {(preview.willGrantInvestorRole || preview.currentRole === "Investment") && (
                  <section className="investment-payment-summary">
                    <div className="investment-payment-summary__title">
                      <BriefcaseBusiness size={18} />
                      <h3>{elakayAutoText("h_915428f577c40a", "Eligibilite au role")}</h3>
                    </div>
                    <div className="investment-payment-line">
                      <span>{elakayAutoText("h_079795644a7adf", "Role actuel")}</span>
                      <strong>{preview.currentRole}</strong>
                    </div>
                    <div className="investment-payment-line">
                      <span>{elakayAutoText("h_f8dc02e46f112b", "Nouveau role apres investissement")}</span>
                      <strong>{preview.projectedRole}</strong>
                    </div>
                    <div className="investment-payment-status-ok">
                      <ShieldCheck size={16} />
                      <span>{preview.willGrantInvestorRole
                        ? elakayAutoText("h_5da90afc487fd0", "Ce projet accorde le statut investisseur apres un investissement reussi.")
                        : elakayAutoText("h_450fb2f9a1ea9c", "Cet investissement ne change pas votre role actuel.")}</span>
                    </div>
                    {preview.currentQuotas && preview.projectedQuotas && (
                      <>
                        <div className="investment-payment-line">
                          <span>{elakayAutoText("h_27e2e24fa069eb", "Quotas actuels")}</span>
                          <strong>
                            {elakayAutoText("h_4dcad22cce25e5", "Max Royaute")} {preview.currentQuotas.maxRoyalite} ·{" "}
                            {elakayAutoText("h_4b5eaa80335509", "Max Vendeurs")} {preview.currentQuotas.maxSeller} ·{" "}
                            {elakayAutoText("h_37590812754592", "Max Fournisseurs")} {preview.currentQuotas.maxProducer} ·{" "}
                            {elakayAutoText("h_7b4138b4721bf8", "Max Associes")} {preview.currentQuotas.maxAssociate}
                          </strong>
                        </div>
                        <div className="investment-payment-line">
                          <span>{elakayAutoText("h_6065ad32d689ae", "Nouveaux quotas estimes")}</span>
                          <strong>
                            {elakayAutoText("h_4dcad22cce25e5", "Max Royaute")} {preview.projectedQuotas.maxRoyalite} ·{" "}
                            {elakayAutoText("h_4b5eaa80335509", "Max Vendeurs")} {preview.projectedQuotas.maxSeller} ·{" "}
                            {elakayAutoText("h_37590812754592", "Max Fournisseurs")} {preview.projectedQuotas.maxProducer} ·{" "}
                            {elakayAutoText("h_7b4138b4721bf8", "Max Associes")} {preview.projectedQuotas.maxAssociate}
                          </strong>
                        </div>
                      </>
                    )}
                  </section>
                )}

                <div className="investment-payment-status-ok">
                  <ShieldCheck size={16} />
                  <span>{elakayAutoText("h_0c0172f4025bd7", "Les donnees definitives seront activees seulement apres confirmation du paiement.")}</span>
                </div>
              </>
            )}
          </div>
        )}

        {step === 2 && (
        <div className="investment-payment-modal__body">
          <section className="investment-payment-summary">
            <div className="investment-payment-summary__title">
              <ReceiptText size={18} />
              <h3>{elakayAutoText("h_4ae1346d8ecb52", "Details du paiement")}</h3>
            </div>
            <div className="investment-payment-line">
              <span>{elakayAutoText("h_17ff00bca5295c", "Projet")}</span>
              <strong>{project.title}</strong>
            </div>
            <div className="investment-payment-line">
              <span>{elakayAutoText("h_f538ff0eb005a2", "Montant a investir")}</span>
              <strong>{formatCurrency(amountNumber, project.currency)}</strong>
            </div>
            <div className="investment-payment-line">
              <span>{elakayAutoText("h_97ecc646d74b31", "Paiements")}</span>
              <strong>{getPaymentFrequencyLabel(project.paymentFrequency)}</strong>
            </div>
            <div className="investment-payment-status-ok">
              <ShieldCheck size={16} />
              <span>{elakayAutoText("h_047bc0a7a75d63", "Contrat lu et accepte avant paiement.")}</span>
            </div>
            {selectedCard && (
              <div className="investment-payment-selected-card">
                <CreditCard size={18} />
                <div>
                  <strong>{getPaymentBrand(selectedCard)} ...{getPaymentLast4(selectedCard)}</strong>
                  <span>{elakayAutoText("h_1092672bc10018", "Expire")} {getPaymentExpiry(selectedCard)}</span>
                </div>
              </div>
            )}
          </section>

          <section className="investment-payment-methods">
            <div className="investment-payment-tabs">
              <button
                type="button"
                className={paymentMode === "saved" ? "active" : ""}
                onClick={() => setPaymentMode("saved")}
                disabled={paymentMethods.length === 0}
              >
                <WalletCards size={16} />
                {elakayAutoText("h_385dfd00829a7d", "Carte sauvegardee")}
              </button>
              <button
                type="button"
                className={paymentMode === "new" ? "active" : ""}
                onClick={() => setPaymentMode("new")}
              >
                <Plus size={16} />
                {elakayAutoText("h_56c2d97b1d4d0e", "Nouvelle carte")}
              </button>
            </div>

            {loadingMethods && (
              <div className="investment-payment-loading">
                <RefreshCw size={16} />
                {elakayAutoText("h_b15a5f705ad5d7", "Chargement des methodes de paiement...")}
              </div>
            )}

            {!loadingMethods && paymentMode === "saved" && (
              <div className="investment-saved-method-list">
                {paymentMethods.length === 0 && (
                  <div className="investment-payment-empty">
                    <CreditCard size={24} />
                    <span>{elakayAutoText("h_2980d0a08471ab", "Aucune carte sauvegardee.")}</span>
                  </div>
                )}
                {paymentMethods.map((method) => (
                  <button
                    type="button"
                    key={method.id}
                    className={`investment-saved-method ${selectedMethodId === method.id ? "active" : ""}`}
                    onClick={() => loadPaymentMethodDetails(method.id)}
                  >
                    <CreditCard size={18} />
                    <span>
                      <strong>{getPaymentBrand(method)} ...{getPaymentLast4(method)}</strong>
                      <em>{elakayAutoText("h_1092672bc10018", "Expire")} {getPaymentExpiry(method)}</em>
                    </span>
                    {selectedMethodId === method.id && <CheckCircle2 size={18} />}
                  </button>
                ))}
              </div>
            )}

            {paymentMode === "new" && (
              <div className="investment-new-card-form">
                <label>
                  <span>{elakayAutoText("h_16d08e0ca62db1", "Nom sur la carte")}</span>
                  <input
                    type="text"
                    value={billingDetails.name}
                    onChange={handleBillingChange("name")}
                    placeholder={elakayAutoText("h_16d08e0ca62db1", "Nom sur la carte")}
                  />
                </label>
                <div className="investment-payment-card-element">
                  <CardElement options={CARD_ELEMENT_OPTIONS} />
                </div>
                <div className="investment-payment-address-grid">
                  <label>
                    <span>{elakayAutoText("h_5ab13f09b3f7e4", "Adresse")}</span>
                    <input type="text" value={billingDetails.line1} onChange={handleBillingChange("line1")} />
                  </label>
                  {/* Country / ZIP / State / City: shared address location fields. */}
                  <AddressLocationSelector
                    idPrefix="investment-billing"
                    value={toLocationValue(billingDetails, STRIPE_FIELDS)}
                    onChange={(location) => setBillingDetails((prev) => fromLocationValue(prev, location, STRIPE_FIELDS))}
                    labels={{ country: elakayAutoText("h_4fa1cbb2f5db0f", "Pays"), city: elakayAutoText("h_f9d6e951f1e062", "Ville") }}
                    classNames={{ field: "investment-billing-field" }}
                  />
                </div>
                <label className="investment-payment-save-card">
                  <input type="checkbox" checked={saveCard} onChange={(event) => setSaveCard(event.target.checked)} />
                  <span>{elakayAutoText("h_536175fa9cdf54", "Sauvegarder cette carte pour mes prochains paiements.")}</span>
                </label>
              </div>
            )}

            <label className="investment-payment-note">
              <span>{elakayAutoText("h_930d94dc3a8525", "Note de paiement")}</span>
              <textarea
                value={paymentNote}
                onChange={(event) => setPaymentNote(event.target.value)}
                rows={3}
                maxLength={500}
                placeholder={elakayAutoText("h_58a85195c94913", "Reference, instruction ou note optionnelle")}
              />
            </label>

            {paymentError && <div className="investment-payment-error">{paymentError}</div>}
            {!paymentError && buttonReason && <div className="investment-payment-warning">{buttonReason}</div>}
          </section>
        </div>
        )}

        <div className="investment-payment-modal__footer">
          {step === 1 ? (
            <>
              <button type="button" className="investment-payment-secondary" onClick={onClose}>
                {elakayAutoText("h_d8d0378aeb8f91", "Fermer")}
              </button>
              <button
                type="button"
                className="investment-payment-primary"
                onClick={() => setStep(2)}
                disabled={!canContinueToPayment}
              >
                {elakayAutoText("h_129ffff9f5ac0f", "Continuer")}
                <ArrowRight size={18} />
              </button>
            </>
          ) : (
            <>
              <button type="button" className="investment-payment-secondary" onClick={() => setStep(1)} disabled={isSubmitting}>
                <ChevronLeft size={18} />
                {elakayAutoText("h_e5befb1f9671ba", "Retour")}
              </button>
              <button type="button" className="investment-payment-secondary" onClick={onClose} disabled={isSubmitting}>
                {elakayAutoText("h_d8d0378aeb8f91", "Fermer")}
              </button>
              <button type="button" className="investment-payment-primary" onClick={handlePaymentSubmit} disabled={Boolean(buttonReason) || isSubmitting}>
                <BadgeDollarSign size={18} />
                {isSubmitting ? elakayAutoText("h_8fad12a8d7bb60", "Preparation...") : elakayAutoText("h_0f1f85d35099ab", "Payer maintenant")}
              </button>
            </>
          )}
        </div>
    </Modal>
  );
};

export const InvestmentHomeSection = ({ data, infoSource}) => {
  const catalog = normalizeCatalog(data);
  const projects = catalog.projects;
  const currentUserInvestments = useMemo(() => {
    const userInfo = infoSource?.userInfo || infoSource?.user || {};
    return Array.isArray(userInfo.investments) ? userInfo.investments : [];
  }, [infoSource?.userInfo, infoSource?.user]);
  const visibleProjects = useMemo(
    () =>
      projects.filter((project) =>
        canViewInvestmentProject({
          project,
          projectVisibilityAccess: project.projectVisibilityAccess,
          visibilityAccessState: project.visibilityAccessState,
          investments: currentUserInvestments,
        })
      ),
    [currentUserInvestments, projects]
  );
  const location = useLocation();
  const navigate = useNavigate();
  const query = new URLSearchParams(location.search);
  const activeType = query.get("projectType") || "";
  const activeMode = query.get("projectMode") || "";
  const [searchText, setSearchText] = useState(query.get("q") || "");

  useEffect(() => {
    setSearchText(query.get("q") || "");
  }, [location.search]);

  const totals = catalog.facets?.totals || {};
  const priorityProjectCount = useMemo(
    () => visibleProjects.filter((project) => getPriorityAccessLabel(project)).length,
    [visibleProjects]
  );
  const typeCounts = useMemo(() => {
    const rows = catalog.facets?.byType || [];
    const map = {};
    rows.forEach((row) => {
      if (row?._id) map[row._id] = row.count || 0;
    });
    return map;
  }, [catalog.facets]);

  const submitSearch = (event) => {
    event.preventDefault();
    navigate(filterUrl(location, { q: searchText.trim() }));
  };

  return (
    <div className="elakay-investment-page">
      <section className="investment-market-hero">
        <div className="investment-shell investment-market-hero__grid">
          <div>
            <p className="investment-eyebrow">{elakayAutoText("h_711fc695d40bc0", "Elakay Investment")}</p>
            <h1>{elakayAutoText("h_8cfbee09004cdd", "Toutes les opportunites d'investissement Elakay")}</h1>
            <p>{elakayAutoText("h_5a20783e7c36b7", "Decouvrez les projets limites, illimites, business deals, real estate, marketplace, digital et revenue sharing dans une seule experience.")}</p>
            <div className="investment-stats-band">
              <div className="investment-stat-tile">
                <strong>{totals.count || catalog.count}</strong>
                <span>{elakayAutoText("h_f96e863d439d95", "Opportunites publiees")}</span>
              </div>
              <div className="investment-stat-tile">
                <strong>{formatCurrency(totals.collectedAmount || 0)}</strong>
                <span>{elakayAutoText("h_50a83de72d59e3", "Capital collecte")}</span>
              </div>
              <div className="investment-stat-tile">
                <strong>{formatCurrency(totals.fundingGoal || 0)}</strong>
                <span>{elakayAutoText("h_30293712e2ae9a", "Objectif global")}</span>
              </div>
            </div>
            {priorityProjectCount > 0 && (
              <div className="investment-priority-strip">
                <ShieldCheck size={16} />
                <span>
                  {priorityProjectCount > 1
                    ? elakayAutoText("h_auto_priority_projects_open_plural", "{count} projets ouverts à vos groupes prioritaires", { count: priorityProjectCount })
                    : elakayAutoText("h_auto_priority_projects_open_singular", "{count} projet ouvert à vos groupes prioritaires", { count: priorityProjectCount })}
                </span>
              </div>
            )}
          </div>
          <form className="investment-search" onSubmit={submitSearch}>
            <input
              value={searchText}
              onChange={(event) => setSearchText(event.target.value)}
              placeholder={elakayAutoText("h_30d8b6af761803", "Chercher un projet, secteur ou modele")}
              aria-label={elakayAutoText("h_9c26a47405b2e6", "Chercher un investissement")}
            />
            <button className="investment-icon-button" type="submit" aria-label={elakayAutoText("h_dcaef5e63af73e", "Chercher")}>
              <Search size={18} />
            </button>
          </form>
        </div>
      </section>

      <section className="investment-filter-band">
        <div className="investment-shell investment-filter-row">
          <span className="investment-chip">
            <Filter size={15} />{elakayAutoText("h_93b9e289e28424", "Types")}</span>
          <Link className={`investment-chip ${!activeType ? "active" : ""}`} to={filterUrl(location, { projectType: "" })}>{elakayAutoText("h_b97ae3b4f9093f", "Tous")}</Link>
          {Object.keys(TYPE_LABELS).map((type) => (
            <Link
              key={type}
              className={`investment-chip ${activeType === type ? "active" : ""}`}
              to={filterUrl(location, { projectType: type })}
            >
              {TYPE_LABELS[type]} {typeCounts[type] ? `(${typeCounts[type]})` : ""}
            </Link>
          ))}
          <Link
            className={`investment-chip ${activeMode === "limited" ? "active" : ""}`}
            to={filterUrl(location, { projectMode: activeMode === "limited" ? "" : "limited" })}
          >
            <Clock3 size={15} />{elakayAutoText("h_23e002b2751a40", "Limite")}</Link>
          <Link
            className={`investment-chip ${activeMode === "unlimited" ? "active" : ""}`}
            to={filterUrl(location, { projectMode: activeMode === "unlimited" ? "" : "unlimited" })}
          >
            <TrendingUp size={15} />{elakayAutoText("h_4196152500a604", "Illimite")}</Link>
        </div>
      </section>
      <section className="investment-shell investment-project-grid">
        {visibleProjects.length === 0 && (
          <div className="investment-empty">
            <h2>{elakayAutoText("h_a95e8e47a8b879", "Aucun investissement public pour ces criteres.")}</h2>
            <p>{elakayAutoText("h_5394f6bc57637d", "Les projets publies apparaitront ici avec leurs objectifs et contrats.")}</p>
          </div>
        )}
        {visibleProjects.map((project) => {
          const progress = getProgress(project);
          const image = getProjectImage(project);
          const imageOpacity = getProjectImageOpacity(project);
          const priorityAccessLabel = getPriorityAccessLabel(project);
          return (
            <Link className="investment-card" to={`/investments/${project.slug || project._id}`} key={project._id || project.slug}>
              <div className="investment-card__media" style={{ "--investment-card-image-opacity": imageOpacity }}>
                {image && <div className="investment-card__media-bg" style={backgroundImageStyle(image)} aria-hidden="true" />}
                {priorityAccessLabel && (
                  <span className="investment-priority-ribbon">
                    <ShieldCheck size={14} />
                    {priorityAccessLabel}
                  </span>
                )}
                <div className="investment-card__badge-row">
                  <span className="investment-badge">{getProjectTypeLabel(project)}</span>
                  <span className="investment-badge">{MODE_LABELS[project.projectMode] || project.projectMode}</span>
                </div>
              </div>
              <div className="investment-card__body">
                <h3>{project.title}</h3>
                {priorityAccessLabel && <span className="investment-card__access-note">{priorityAccessLabel}</span>}
                <p>{compactText(project.summary || project.description || project.category, 155)}</p>
                <div className="investment-progress">
                  <div className="investment-progress__meta">
                    <span>{formatCurrency(project.collectedAmount, project.currency)} {elakayAutoText("h_auto_collected_lower", "collecte")}</span>
                    <span>{Math.round(progress)}%</span>
                  </div>
                  <div className="investment-progress__bar" style={{ "--progress": `${progress}%` }}>
                    <span />
                  </div>
                </div>
                <div className="investment-card__facts">
                  <div className="investment-card__fact">
                    <span>{elakayAutoText("h_47d1c260dca74b", "Objectif")}</span>
                    <strong>{project.fundingGoal ? formatCurrency(project.fundingGoal, project.currency) : elakayAutoText("h_0201d81046713f", "Ouvert")}</strong>
                  </div>
                  <div className="investment-card__fact">
                    <span>{elakayAutoText("h_5d398eef3ecc3e", "Minimum")}</span>
                    <strong>{formatCurrency(project.minimumInvestment, project.currency)}</strong>
                  </div>
                  <div className="investment-card__fact">
                    <span>{elakayAutoText("h_b34c4ee8775e03", "Frequence")}</span>
                    <strong>{getPaymentFrequencyLabel(project.paymentFrequency)}</strong>
                  </div>
                  <div className="investment-card__fact">
                    <span>{elakayAutoText("h_df3a311c680bfa", "Debut revenus")}</span>
                    <strong>{formatDate(project.paymentStartDate)}</strong>
                  </div>
                </div>
                <div className="investment-card__footer">
                  <span>{elakayAutoText("h_e0f02bbd28e1ff", "Voir l'investissement")}</span>
                  <ArrowRight size={18} />
                </div>
              </div>
            </Link>
          );
        })}
        {visibleProjects.length > 0 && (catalog.skip > 0 || catalog.skip + catalog.limit < catalog.count) && (
          <div className="investment-pagination">
            <Link
              className={`investment-page-btn ${catalog.skip <= 0 ? "disabled" : ""}`}
              to={catalog.skip > 0 ? paginationUrl(location, Math.max(catalog.skip - catalog.limit, 0)) : "#"}
              aria-disabled={catalog.skip <= 0}
            >
              <ChevronLeft size={16} />
              {elakayAutoText("h_b962f0ffdefee4", "Precedent")}
            </Link>
            <Link
              className={`investment-page-btn ${catalog.skip + catalog.limit >= catalog.count ? "disabled" : ""}`}
              to={catalog.skip + catalog.limit < catalog.count ? paginationUrl(location, catalog.skip + catalog.limit) : "#"}
              aria-disabled={catalog.skip + catalog.limit >= catalog.count}
            >
              {elakayAutoText("h_596d29a7e33c76", "Next")}
              <ChevronRight size={16} />
            </Link>
          </div>
        )}
      </section>
    </div>
  );
};

// Mirrors server_elakay/elakay_services/investmentSummary.js's
// willGrantInvestorRole exactly: only Family/Administrator (higher than
// Investment in the role hierarchy) are protected, and an already-Investment
// user isn't re-granted. Everything else -- Royalty/Seller/Provider/
// Associate, and a brand-new "User" with no role yet -- is eligible, so this
// is a deny-list, not an allow-list (a bare user has no name to match here).
const AUTO_INVESTOR_PROTECTED_ROLES = ["Administrator", "Family", "Familly"];

export const InvestmentDetailSection = ({ data, infoSource }) => {
  const payload = data?.project ? data : { project: data };
  const project = payload?.project;
  const navigate = useNavigate();
  const auth = isAuthenticated();
  const [amount, setAmount] = useState("");
  const [contractAccepted, setContractAccepted] = useState(false);
  const [contractRead, setContractRead] = useState(false);
  const [contractModalOpen, setContractModalOpen] = useState(false);
  const [activeDocument, setActiveDocument] = useState(null);
  const [paymentModalOpen, setPaymentModalOpen] = useState(false);
  const [showInvestorExplainer, setShowInvestorExplainer] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  // Display-only eligibility check -- the backend independently enforces the
  // same rule (autoBecomeInvestor + eligible role + not already Investor/
  // Administrator/Family) before actually granting anything.
  const currentWallRole = getCurrentWallRoleName(infoSource);
  const eligibleForAutoInvestor = Boolean(project?.autoBecomeInvestor)
    && !AUTO_INVESTOR_PROTECTED_ROLES.includes(currentWallRole)
    && currentWallRole !== "Investment";

  useEffect(() => {
    if (project?.minimumInvestment) setAmount(String(project.minimumInvestment));
    setContractAccepted(false);
    setContractRead(false);
    setContractModalOpen(false);
    setActiveDocument(null);
    setPaymentModalOpen(false);
    setMessage("");
    setError("");
  }, [project?._id]);

  // Escape-to-close is now handled natively by react-bootstrap's Modal
  // (keyboard prop, default true) -- no custom listener needed.

  if (!project) {
    return (
      <div className="elakay-investment-page">
        <div className="investment-shell investment-empty">
          <h2>{elakayAutoText("h_de23230c46df62", "Investissement introuvable.")}</h2>
          <Link className="investment-action" to="/investments">{elakayAutoText("h_98a1f4773ee073", "Retour aux investissements")}</Link>
        </div>
      </div>
    );
  }

  const progress = getProgress(project);
  const image = getProjectImage(project);
  const investorCount = payload?.investorCount || 0;
  const priorityAccessLabel = getPriorityAccessLabel(project);
  const contractDocument = getContractDocument(project);
  const contractDocumentTypeLabel = getDocumentTypeLabel(contractDocument || { documentType: "contract" });
  const contractDocumentKindLabel = getDocumentKindLabel(
    contractDocument || { kind: project.contractPDF ? "pdf" : "document" }
  );
  const contractButtonLabel = `${contractDocumentTypeLabel} · ${contractDocumentKindLabel}`;
  const canInvest = contractRead && contractAccepted && !submitting;
  const modalDocument = activeDocument || contractDocument || {
    title: elakayAutoText("h_d008bdd9b9811d", "Contrat"),
    documentType: "contract",
    kind: project.contractPDF ? "pdf" : "document",
    url: project.contractPDF || "",
  };
  const modalDocumentTypeLabel = getDocumentTypeLabel(modalDocument);
  const modalDocumentKindLabel = getDocumentKindLabel(modalDocument);
  const modalDocumentLabel = `${modalDocumentTypeLabel} · ${modalDocumentKindLabel}`;

  function closeDocumentModal() {
    setContractModalOpen(false);
    setActiveDocument(null);
  }

  const openDocumentModal = (document) => {
    setActiveDocument(document);
    setContractModalOpen(true);
  };

  const openContractModal = () => {
    setContractRead(true);
    setActiveDocument(contractDocument || {
      title: elakayAutoText("h_d008bdd9b9811d", "Contrat"),
      documentType: "contract",
      kind: project.contractPDF ? "pdf" : "document",
      url: project.contractPDF || "",
      richDocument: project.contractJSON,
    });
    setContractModalOpen(true);
    setError("");
  };

  const getInvestmentAmount = () => {
    const investmentAmount = Number(amount);
    if (!Number.isFinite(investmentAmount) || investmentAmount <= 0) return 0;
    return investmentAmount;
  };

  const openPaymentModal = () => {
    setError("");
    setMessage("");
    if (!auth?.token) {
      navigate("/signin", { state: { from: `/investments/${project.slug || project._id}` } });
      return;
    }
    if (!contractRead) {
      setError(elakayAutoText("h_dfa7318498d599", "Vous devez lire le contrat avant de continuer."));
      return;
    }
    if (!contractAccepted) {
      setError(elakayAutoText("h_dc70f4d685dbd7", "Vous devez accepter le contrat avant de continuer."));
      return;
    }
    if (!getInvestmentAmount()) {
      setError(elakayAutoText("h_e1d3c8f4466b6e", "Montant d'investissement invalide."));
      return;
    }
    if (eligibleForAutoInvestor) {
      setShowInvestorExplainer(true);
      return;
    }
    setPaymentModalOpen(true);
  };

  const renderDocumentModalContent = (document = modalDocument) => {
    const url = document?.url;
    const kind = String(document?.kind || "").toLowerCase();
    const imageDocument =
      kind === "image" || /\.(png|jpe?g|webp|gif|svg)(\?.*)?$/i.test(String(url || ""));

    if (url && imageDocument) {
      return (
        <div className="investment-contract-image-wrap">
          <img className="investment-contract-image" src={url} alt={getDocumentTitle(document)} />
        </div>
      );
    }

    if (url && isOfficeDocumentUrl(url, kind)) {
      // Browsers have no native .doc/.docx renderer -- pointing an iframe
      // directly at the file makes most browsers download it instead of
      // showing it. Office Online Viewer renders it in place, no download.
      return (
        <iframe
          className="investment-contract-frame"
          src={`https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(url)}`}
          title={getDocumentTitle(document)}
        />
      );
    }

    if (url) {
      return (
        <iframe
          className="investment-contract-frame"
          src={url}
          title={getDocumentTitle(document)}
        />
      );
    }

    if (document?.documentType !== "contract" && !document?.richDocument) {
      return (
        <div className="investment-contract-empty">
          <FileText size={34} />
          <p>{elakayAutoText("h_352f9d1937f9df", "Ce document n'a pas de fichier disponible pour l'affichage.")}</p>
        </div>
      );
    }

    return (
      <div className="investment-contract-rich-document">
        {renderRichDocument(
          document?.richDocument || project.contractJSON,
          "Le contrat structure les risques, obligations, revenus, dates et clauses legales."
        )}
      </div>
    );
  };

  const handleInvest = async ({
    paymentMethodId,
    paymentMode,
    paymentNote,
    billingDetails,
    selectedPaymentMethod,
    stripe,
  }) => {
    setError("");
    setMessage("");
    if (!auth?.token) {
      navigate("/signin", { state: { from: `/investments/${project.slug || project._id}` } });
      return;
    }
    if (!contractRead) {
      setError(elakayAutoText("h_dfa7318498d599", "Vous devez lire le contrat avant de continuer."));
      return;
    }
    if (!contractAccepted) {
      setError(elakayAutoText("h_dc70f4d685dbd7", "Vous devez accepter le contrat avant de continuer."));
      return;
    }
    const investmentAmount = getInvestmentAmount();
    if (!investmentAmount) {
      setError(elakayAutoText("h_e1d3c8f4466b6e", "Montant d'investissement invalide."));
      return;
    }
    if (!paymentMethodId) {
      throw new Error(elakayAutoText("h_a277aa2a4bba3b", "Selectionnez une methode de paiement."));
    }
    setSubmitting(true);
    try {
      const result = await createInvestmentPaymentIntent(project._id, {
        amount: investmentAmount,
        contractAccepted: true,
        paymentMethodId,
        metadata: {
          contractRead: true,
          contractDocumentType: contractDocumentTypeLabel,
          contractDocumentKind: contractDocumentKindLabel,
          contractDocumentUrl: contractDocument?.url || "",
          contractSource: contractDocument?.url
            ? "document"
            : hasRichDocument(project.contractJSON)
              ? "contractJSON"
              : "fallback",
          paymentMode: paymentMode || "",
          paymentNote: paymentNote || "",
          billingName: billingDetails?.name || "",
          billingCountry: billingDetails?.country || "",
          paymentMethodBrand: selectedPaymentMethod ? getPaymentBrand(selectedPaymentMethod) : "",
          paymentMethodLast4: selectedPaymentMethod ? getPaymentLast4(selectedPaymentMethod) : "",
        },
      }, auth.token);

      let paymentIntentStatus = result.paymentStatus;
      if (result.clientSecret && stripe && paymentIntentStatus !== "succeeded") {
        const confirmation = await stripe.confirmCardPayment(result.clientSecret);
        if (confirmation.error) throw new Error(confirmation.error.message);
        paymentIntentStatus = confirmation.paymentIntent?.status || paymentIntentStatus;
      }

      if (paymentIntentStatus === "succeeded" && result.investment?._id) {
        const confirmResult = await confirmInvestmentPayment({
          investmentId: result.investment._id,
          stripePaymentIntentId: result.paymentIntentId,
        }, auth.token);

        // Refresh the profile so a newly-activated role/quota (or an
        // updated cumulative total/percentage) shows up immediately,
        // without requiring a manual page reload or re-login -- the server
        // is the source of truth, this just re-pulls it.
        if (auth?.token && typeof infoSource?.readUser === "function") {
          await infoSource.readUser(auth.token);
        }

        const confirmedInvestment = confirmResult?.investment || result.investment;
        const remaining = project.maximumInvestment
          ? Math.max(project.maximumInvestment - Number(confirmedInvestment?.investedAmount || 0), 0)
          : null;
        setMessage(elakayAutoText(
          "h_aa56a0f8204c74",
          "Paiement confirme : total investi {total}, propriete {percent}%, {remaining} restant disponible pour ce projet.",
          {
            total: formatCurrency(confirmedInvestment?.investedAmount, project.currency),
            percent: confirmedInvestment?.ownershipPercent ?? 0,
            remaining: remaining !== null ? formatCurrency(remaining, project.currency) : elakayAutoText("h_8bb749a035fbbe", "Flexible"),
          }
        ) + (confirmResult?.investorRoleGranted
          ? ` ${elakayAutoText("h_4dc0582a023da8", "Le statut investisseur a ete active suite a ce paiement.")}`
          : ""));
      } else if (result.clientSecret) {
        setMessage(elakayAutoText("h_cdf11e210912c8", "Paiement Stripe prepare. Le module de paiement peut utiliser le client secret retourne par l'API."));
      } else {
        setMessage(elakayAutoText("h_903f1ec4acc994", "Demande d'investissement creee."));
      }
      setPaymentModalOpen(false);
      return result;
    } catch (err) {
      setError(err.message || elakayAutoText("h_fe164b7760e10a", "Impossible de preparer le paiement."));
      throw err;
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="elakay-investment-page">
      <section className="investment-detail-hero">
        <div className="investment-shell investment-detail-hero__inner">
          <div className="investment-detail-media">
            {image && <img src={image} alt={project.title} />}
          </div>
          <div className="investment-detail-title">
            <p className="investment-eyebrow">{getProjectTypeLabel(project)}</p>
            <h1>{project.title}</h1>
            {priorityAccessLabel && (
              <div className="investment-priority-banner">
                <ShieldCheck size={16} />
                <span>{priorityAccessLabel}</span>
              </div>
            )}
            <p>{project.summary}</p>
            <div className="investment-stats-band">
              <div className="investment-stat-tile">
                <strong>{formatCurrency(project.collectedAmount, project.currency)}</strong>
                <span>{elakayAutoText("h_aa7d0813d15fd1", "Collecte")}</span>
              </div>
              <div className="investment-stat-tile">
                <strong>{project.fundingGoal ? formatCurrency(project.fundingGoal, project.currency) : elakayAutoText("h_0201d81046713f", "Ouvert")}</strong>
                <span>{elakayAutoText("h_47d1c260dca74b", "Objectif")}</span>
              </div>
              <div className="investment-stat-tile">
                <strong>{investorCount}</strong>
                <span>{elakayAutoText("h_69ca7dcd49ee2e", "Investisseurs")}</span>
              </div>
            </div>
            <div className="investment-detail-panel" style={{ marginTop: 14 }}>
              <h2>{elakayAutoText("h_00ca7010ae8f49", "Investir dans ce projet")}</h2>
              <div className="investment-amount-grid">
                <input
                  type="number"
                  min={project.minimumInvestment || 0}
                  max={project.maximumInvestment || undefined}
                  value={amount}
                  onChange={(event) => setAmount(event.target.value)}
                  aria-label={elakayAutoText("h_f538ff0eb005a2", "Montant a investir")}
                />
                <div className="investment-card__fact">
                  <span>{elakayAutoText("h_5d398eef3ecc3e", "Minimum")}</span>
                  <strong>{formatCurrency(project.minimumInvestment, project.currency)}</strong>
                </div>
                <div className="investment-card__fact">
                  <span>{elakayAutoText("h_a8df2fec28d894", "Maximum")}</span>
                  <strong>{project.maximumInvestment ? formatCurrency(project.maximumInvestment, project.currency) : elakayAutoText("h_8bb749a035fbbe", "Flexible")}</strong>
                </div>
                <div className="investment-contract-gate">
                  <button
                    className={`investment-document-button ${contractRead ? "is-read" : ""}`}
                    type="button"
                    onClick={openContractModal}
                    aria-label={elakayAutoText("h_d47c2e833d2e42", "Afficher le contrat")}
                  >
                    <FileText size={18} />
                    <span>
                      <strong>{contractDocument?.title || elakayAutoText("h_d008bdd9b9811d", "Contrat")}</strong>
                      <em>{contractButtonLabel}</em>
                    </span>
                    {contractRead ? <CheckCircle2 size={18} /> : <Eye size={18} />}
                  </button>
                  <label className={`investment-checkbox investment-contract-accept ${!contractRead ? "is-disabled" : ""}`}>
                    <input
                      type="checkbox"
                      checked={contractAccepted}
                      disabled={!contractRead}
                      onChange={(event) => setContractAccepted(event.target.checked)}
                    />
                    <span>{elakayAutoText("h_4f3a240c992283", "J'ai lu et accepte le contrat, les risques, la frequence de paiement et les conditions du projet.")}</span>
                  </label>
                  <div className="investment-contract-status">
                    <span className={contractRead ? "is-active" : ""}>
                      {contractRead ? <CheckCircle2 size={15} /> : <Eye size={15} />}
                      {elakayAutoText("h_5a1261705c59bf", "Contrat lu")}
                    </span>
                    <span className={contractAccepted ? "is-active" : ""}>
                      {contractAccepted ? <CheckCircle2 size={15} /> : <Check size={15} />}
                      {elakayAutoText("h_dee5d1d3cf23be", "Contrat accepte")}
                    </span>
                  </div>
                </div>
                <button className="investment-action" type="button" onClick={openPaymentModal} disabled={!canInvest}>
                  <BadgeDollarSign size={18} />
                  {submitting ? elakayAutoText("h_8fad12a8d7bb60", "Preparation...") : elakayAutoText("h_3ae1ad2194c116", "Investir avec Stripe")}
                </button>
                {message && <div className="investment-form-message">{message}</div>}
                {error && <div className="investment-form-message error">{error}</div>}
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="investment-shell investment-detail-layout">
        <div className="investment-detail-content">
          <div className="investment-info-section">
            <h2>{elakayAutoText("h_23bca783e86c89", "Vue d'ensemble")}</h2>
            {renderRichDocument(project.descriptionJSON, project.summary || "Description du projet a venir.")}
          </div>
          <div className="investment-info-section">
            <h2>{elakayAutoText("h_40aaecb478f4d3", "Points forts")}</h2>
            <ul>
              {(project.highlights?.length ? project.highlights : project.keyFeatures || []).map((item, index) => (
                <li key={index}>{item}</li>
              ))}
            </ul>
          </div>
          <div className="investment-info-section">
            <h2>{elakayAutoText("h_d008bdd9b9811d", "Contrat")}</h2>
            {renderRichDocument(project.contractJSON, "Le contrat structure les risques, obligations, revenus, dates et clauses legales.")}
          </div>
          {project.documents?.length > 0 && (
            <div className="investment-info-section">
              <h2>{elakayAutoText("h_687c82861c956e", "Documents")}</h2>
              <div className="investment-document-list">
                {project.documents.map((document, index) => (
                  <button
                    type="button"
                    className="investment-document-link"
                    onClick={() => openDocumentModal(document)}
                    key={`${document.url}-${index}`}
                  >
                    <span>
                      <FileText size={17} />
                      <strong>{getDocumentTitle(document, elakayAutoText("h_e214b8a2992305", "Document"))}</strong>
                    </span>
                    <em>{getDocumentTypeLabel(document)} · {getDocumentKindLabel(document)}</em>
                    <Eye size={16} />
                  </button>
                ))}
              </div>
            </div>
          )}
          <div className="investment-info-section">
            <h2>{elakayAutoText("h_601246900653a9", "Risques")}</h2>
            <ul>
              {(project.risks?.length ? project.risks : [
                "Le capital investi peut diminuer.",
                "Les retours ne sont pas garantis.",
                "Les distributions dependent de la performance du projet.",
              ]).map((item, index) => (
                <li key={index}>{item}</li>
              ))}
            </ul>
          </div>
        </div>

        <aside className="investment-side-stack">
          <div className="investment-side-card">
            <h3>{elakayAutoText("h_e1282d32766ff2", "Financement")}</h3>
            <div className="investment-progress">
              <div className="investment-progress__meta">
                <span>{Math.round(progress)}{elakayAutoText("h_df33b7c3d3f259", "% finance")}</span>
                <span>{formatCurrency(project.remainingAmount, project.currency)} {elakayAutoText("h_auto_remaining_lower", "restant")}</span>
              </div>
              <div className="investment-progress__bar" style={{ "--progress": `${progress}%` }}>
                <span />
              </div>
            </div>
          </div>
          <div className="investment-side-card">
            <h3>{elakayAutoText("h_54937b3a4f8cfa", "Informations")}</h3>
            <div className="investment-kv">
              <div><span><Building2 size={14} />{elakayAutoText("h_65a6bdd2dd521b", "Secteur")}</span><strong>{project.category || elakayAutoText("h_6abba28e7f9a6b", "A definir")}</strong></div>
              <div><span><BriefcaseBusiness size={14} />{elakayAutoText("h_9aea275244b933", "Modele")}</span><strong>{getProjectTypeLabel(project)}</strong></div>
              <div><span><Landmark size={14} />{elakayAutoText("h_a7b93d2128e820", "Mode")}</span><strong>{MODE_LABELS[project.projectMode] || project.projectMode}</strong></div>
              <div><span><CalendarClock size={14} />{elakayAutoText("h_97ecc646d74b31", "Paiements")}</span><strong>{getPaymentFrequencyLabel(project.paymentFrequency)}</strong></div>
              <div><span><Clock3 size={14} />{elakayAutoText("h_df3a311c680bfa", "Debut revenus")}</span><strong>{formatDate(project.paymentStartDate)}</strong></div>
            </div>
          </div>
          <div className="investment-alert">
            <CheckCircle2 size={15} />{elakayAutoText("h_af2a4fd72d6179", "Ne pas investir si vous n'etes pas pret a perdre tout ou partie du capital investi.")}</div>
        </aside>
      </section>
      <Modal
        show={contractModalOpen}
        onHide={closeDocumentModal}
        centered
        dialogClassName="investment-contract-modal-dialog"
        contentClassName="investment-contract-modal-content"
        aria-labelledby="investment-contract-modal-title"
      >
        <Modal.Header closeButton className="investment-contract-modal__header">
          <div>
            <Modal.Title id="investment-contract-modal-title">
              {getDocumentTitle(modalDocument, elakayAutoText("h_e214b8a2992305", "Document"))}
            </Modal.Title>
            <span>{modalDocumentLabel}</span>
          </div>
        </Modal.Header>
        <Modal.Body className="investment-contract-modal__body">
          {renderDocumentModalContent(modalDocument)}
        </Modal.Body>
      </Modal>
      <Modal
        show={showInvestorExplainer}
        onHide={() => setShowInvestorExplainer(false)}
        centered
        dialogClassName="investment-investor-modal-dialog"
        contentClassName="investment-investor-modal"
        aria-labelledby="investment-investor-modal-title"
      >
        <Modal.Header className="investment-contract-modal__header">
          <div>
            <Modal.Title id="investment-investor-modal-title">
              {elakayAutoText("h_invBecomeInvestorTitle", "Vous deviendrez automatiquement Investor dans Ilakay")}
            </Modal.Title>
          </div>
          <button type="button" className="investment-modal-close" onClick={() => setShowInvestorExplainer(false)} aria-label={elakayAutoText("h_d8d0378aeb8f91", "Fermer")}>
            <X size={20} />
          </button>
        </Modal.Header>
        <Modal.Body className="investment-contract-modal__body investment-investor-modal__body">
          {/* Reuses the same Investor role CMS content already shown
              elsewhere in the app (stepAboutUser.jsx's renderContent) --
              no new copy, no invented commission numbers. */}
          <Pages classAlert="container" paragra0={infoSource?.laguangeElakay?.("planProInvestElakay", infoSource?.languageAPI)} />
          <Pages classAlert="container" paragra0={infoSource?.laguangeElakay?.("termConditionInvest", infoSource?.languageAPI)} />
          {project.investorBonusPercent > 0 && (
            <p className="investment-investor-modal__fact">
              {elakayAutoText("h_invProjectBonus", "Bonus specifique a ce projet")}: <strong>{project.investorBonusPercent}%</strong>
            </p>
          )}
          <p className="investment-investor-modal__fact">
            {elakayAutoText("h_invYourLimit", "Votre limite calculee pour ce montant")}:{" "}
            <strong>
              {calculateProportionalValue(
                getInvestmentAmount(),
                project.minimumInvestment,
                project.maximumInvestment,
                project.investmentLevels?.roleTiers?.[currentWallRole]?.maxAtMin,
                project.investmentLevels?.roleTiers?.[currentWallRole]?.maxAtMax
              )}
            </strong>
          </p>
        </Modal.Body>
        <Modal.Footer className="investment-investor-modal__footer">
          <button type="button" className="investment-action investment-action--secondary" onClick={() => setShowInvestorExplainer(false)}>
            {elakayAutoText("h_cancel", "Annuler")}
          </button>
          <button
            type="button"
            className="investment-action"
            onClick={() => { setShowInvestorExplainer(false); setPaymentModalOpen(true); }}
          >
            {elakayAutoText("h_confirm", "Continuer")}
          </button>
        </Modal.Footer>
      </Modal>
      {paymentModalOpen && (
        <InvestmentPaymentModalFrame
          project={project}
          amount={getInvestmentAmount()}
          authToken={auth?.token}
          submitting={submitting}
          onClose={() => !submitting && setPaymentModalOpen(false)}
          onSubmitPayment={handleInvest}
        />
      )}
    </div>
  );
};

export default InvestmentHomeSection;
